<template>
  <div class="d-flex px-2 flex-column justify-center">

    <div class="pagination">
      <div class="d-flex justify-center my-1 align-center">
        <v-btn small class="mx-2 black--text" color="#ffbf00" :disabled="currentPage <= 1" @click="movePage(-1)">
          <v-icon x-small>mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn small class="mx-2 black--text" color="#ffbf00" :disabled="currentPage >= filteredPages"
          @click="movePage(1)">
          <v-icon x-small>mdi-arrow-right</v-icon>
        </v-btn>
      </div>
      <div v-show="filteredCount > 0" class="pagination-numbers">
        <div></div>
        <div class="d-flex pagination-status align-center justify-center white--text">
          <span>{{ $t('flowSight.page') }}</span>
          <input name="Page Number" :value="currentPage" type="number" class="page-input mx-2 white--text darken-4"
            :min="filteredPages > 0 ? 1 : 0" :max="filteredPages"
            :style="{ width: 'calc(' + pageInputWidth + 'ch + 0.8rem)' }" @change="onPageInput($event)"
            @keyup.enter="onPageInput($event)" @blur="onPageInput($event)" />
          <span>{{ $t('flowSight.of') }} {{ filteredPages }}</span>
        </div>
        <span class="pagination-items text-caption">{{ firstItem }} - {{ lastItem }} {{ $t('flowSight.of') }} {{ filteredCount }}</span>
      </div>
    </div>

    <table v-if="visibleColumns.length">
      <thead>
        <tr>
          <th v-for="col in visibleColumns" :key="col">
            {{ col.toUpperCase() }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(row, ri) in pagedRows" :key="ri">
          <td v-for="(col, ci) in visibleColumns" :key="col">
            <span :class="cellClass(row[col])">{{ formatCell(row, col, ci) }}</span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import * as dc from "dc";

// Rows per page.
const PAGE_SIZE = 30;

// Hide these columns.
const EXCLUDED_COLUMNS = new Set([
  "id", "rfq_id", "tenorindex", "tenor", "tenordays",
  "convflag", "volbucket", "yieldccy", "yieldclient",
]);

const RESPONSIVE_HIDDEN_COLUMNS = {
  smAndDown: new Set(["cnpj", "currency", "fwdclient", "yieldbrlcost", "spotfx", "fwd"]),
};

// Maps raw cell values to CSS class names for status badges.
const STATUS_CLASSES = {
  "BUY": "buy-color",
  "Compra": "buy-color",
  "SELL": "sell-color",
  "Venda": "sell-color",
  "REJECTED": "rejected",
  "REJEITADO": "rejected",
};

// Regexes are compiled once here. Inlining them inside a
// function would cause them to be re-compiled on every render
const RE_DIGITS_ONLY = /^\d+$/; // string is all digits
const RE_DATE_PATTERN = // basic date pattern check (e.g. "Mon Sep", "2025-10-", "2025/10/")
  /[A-Za-z]{3}\s[A-Za-z]{3}\s\d{2}|^\d{4}[-/]\d{2}[-/]\d{2}/;

function isProbablyDate(val) {
  if (val instanceof Date) return true;
  if (typeof val !== "string") return false;
  if (RE_DIGITS_ONLY.test(val)) return false; // only allow date-like strings, not numbers
  return RE_DATE_PATTERN.test(val);
}

// Converts any date-like value to the local DD/MM/YYYY display format.
function formatDate(val) {
  var date = new Date(val);
  if (isNaN(date.getTime())) return String(val);
  return (
    String(date.getDate()).padStart(2, "0") + "/" +
    String(date.getMonth() + 1).padStart(2, "0") + "/" +
    date.getFullYear()
  );
}

// The last column often contains free-text RFQ comments that are prepended
// with Bloomberg identifiers (BLP., BLP.RFQ, rfq_id). This function strips
// those prefixes for cleaner display and capitalises the first character.
function cleanLastColumn(val) {
  var text = String(val).trim();
  var words = text.split(/\s+/);
  var cleaned = words.filter(function (w) {
    return !w.includes("BLP.") && !w.includes("BLP.RFQ") &&
      !w.includes("rfq_id") && !w.includes("rfq.");
  });
  var legend =
    (text.includes("rfq_id") && text.includes("BLP.")) || text.includes("BLP.RFQ")
      ? cleaned.join(" ")
      : text;
  return legend.length > 0 ? legend[0].toUpperCase() + legend.slice(1) : "";
}

export default {
  props: {
    // Object containing crossfilter dimensions.
    // We use dim.dimTable (an index dimension) as the data cursor for paginated reads.
    dim: { type: Object, required: true },
    // raw { rows: Array } snapshot used to derive visible columns and totalCount.
    // It does NOT drive per-render row reads; crossfilter does that via dim.dimTable.
    tableData: { type: Object, required: true },
    // The shared crossfilter instance.
    ndx: { type: Object, default: function () { return {}; } },
    chartGroup: { type: String, default: undefined },
  },

  // ── Local state ───────────────────────────────────────────────────────────
  data: function () {
    return {
      currentPage: 1,
      revision: 0,
    };
  },

  // ── Computed properties ───────────────────────────────────────────────────
  computed: {
    // Total rows in the crossfilter index, regardless of any active filters.
    // Falls back to tableData.rows.length if ndx is not yet initialised.
    totalCount: function () {
      if (this.ndx && typeof this.ndx.size === "function") return this.ndx.size();
      return Array.isArray(this.tableData && this.tableData.rows)
        ? this.tableData.rows.length
        : 0;
    },

    firstItem() {
      if (this.filteredCount === 0) return 0;
      return (this.currentPage - 1) * PAGE_SIZE + 1
    },

    lastItem() {
      return Math.min(this.currentPage * PAGE_SIZE, this.filteredCount);
    },

    // Rows currently passing ALL active filters (date, side, chart clicks…).
    filteredCount: function () {
      void this.revision; // listen to dc redraws
      if (!this.ndx || typeof this.ndx.groupAll !== "function") return 0;
      return this.ndx.groupAll().value();
    },

    // Page counts derived from the two row counts above.
    totalPages: function () {
      return Math.ceil(this.totalCount / PAGE_SIZE);
    },
    filteredPages: function () {
      return Math.ceil(this.filteredCount / PAGE_SIZE);
    },

    // Character width of the page input box, sized to fit the TOTAL page
    // count rather than the filtered count. If we used filteredPages the box
    // would visually resize every time a filter is applied, which is jarring.
    pageInputWidth: function () {
      return String(Math.max(this.totalPages, 1)).length;
    },

    responsiveHiddenColumns: function () {
      const breakpoint = this.$vuetify.breakpoint;
      console.log('breaki', breakpoint)
      if (breakpoint.mobile) { console.log('small'); return RESPONSIVE_HIDDEN_COLUMNS.smAndDown;} // mobile and tabld
      //if (breakpoint.mdAndDown) return RESPONSIVE_HIDDEN_COLUMNS.mdAndDown;
      return new Set();
    },

    visibleColumns: function () {
      var rows = this.tableData && this.tableData.rows;
      if (!Array.isArray(rows) || rows.length === 0) return [];
      var hidden = this.responsiveHiddenColumns;
      return Object.keys(rows[0]).filter(function (col) {
        var key = col.toLowerCase();
        if (EXCLUDED_COLUMNS.has(key)) return false;
        if (hidden.has(key)) return false;
        return rows.some(function (row) {
          var v = row[col];
          return v !== null && v !== undefined && String(v).trim() !== "";
        });
      });
    },

    // The actual rows to display on the current page. This is the only computed
    // property that reads from the crossfilter dimension (dim.dimTable).
    pagedRows: function () {
      void this.revision; // listen to dc redraws
      var dim = this.dim || {};
      if (!dim.dimTable || this.filteredCount === 0) return [];
      var offset = Math.max((this.currentPage - 1) * PAGE_SIZE, 0);
      return dim.dimTable.bottom(PAGE_SIZE, offset);
    },
  },

  watch: {
    // When the parent loads a new dataset entirely, reset to page 1.
    tableData: function (newData) {
      if (newData && newData.rows && newData.rows.length > 0) {
        this.currentPage = 1;
      }
    },

    // Auto-clamp currentPage when a filter changes the number of pages.
    filteredPages: function (pages) {
      if (pages === 0) {
        this.currentPage = 0; // no rows at all - Show page 0 of 0
      } else if (this.currentPage > pages) {
        this.currentPage = pages; // current page was beyond new max
      } else if (this.currentPage === 0 && pages > 0) {
        this.currentPage = 1; // filter was lifted, restore page 1
      }
    },
  },

  mounted: function () {
    this._dcListener = {
      render: () => { this.revision++; },
      redraw: () => { this.revision++; },
      filterAll: () => { },
      // anchorName() must return a unique string; dc uses it as a key in its
      // internal chart registry. _uid is Vue's guaranteed per-instance integer.
      anchorName: () => { return "dataTableVue_" + this._uid; },
    };
    dc.registerChart(this._dcListener, this.chartGroup);
  },

  beforeDestroy: function () {
    if (this._dcListener) {
      dc.deregisterChart(this._dcListener, this.chartGroup);
    }
  },

  methods: {
    // Ensures a page number is within [1, filteredPages]. Returns 0 only
    // when there are no pages at all (empty / fully filtered out dataset).
    clampPage: function (page) {
      var max = this.filteredPages;
      if (max <= 0) return 0;
      if (!Number.isFinite(page)) return 1;
      return Math.min(Math.max(Math.floor(page), 1), max);
    },

    goToPage: function (page) {
      this.currentPage = this.clampPage(page);
    },

    // Called by the ← / → buttons. delta is +1 or -1.
    movePage: function (delta) {
      this.goToPage(this.currentPage + delta);
    },

    onPageInput: function (event) {
      var clamped = this.clampPage(Number(event.target.value));
      this.currentPage = clamped;
      event.target.value = String(clamped);
    },

    // O(1) lookup for status badge classes. See STATUS_CLASSES.
    cellClass: function (value) {
      return STATUS_CLASSES[value] || "";
    },

    // Pure display formatter. Called once per cell during template rendering.
    // Order of precedence:
    //   1. Empty / null  → blank string
    //   2. Date-like     → DD/MM/YYYY
    //   3. Last column   → strip RFQ prefixes, capitalise
    //   4. Everything else → plain string
    formatCell: function (row, col, colIndex) {
      var val = row[col];
      if (val == null || String(val).trim() === "") return "";
      if (isProbablyDate(val)) return formatDate(val);
      if (colIndex === this.visibleColumns.length - 1) return cleanLastColumn(val);
      return String(val);
    },
  },
};
</script>

<style scoped>
table {
  border: 1px solid var(--muted);
  font-size: 12px;
  padding: .1rem;
  border-radius: 7px;
  width: 100%;
  min-width: auto;
}

.pagination-numbers {
  margin-top: .5rem;
  margin-bottom: .6rem;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  align-items: center;
  width: 100%;
}

.pagination-status {
  grid-column: 2;
  display: flex;
  align-items: center;
  justify-content: center;
}

.pagination {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.pagination-items {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin: 0 .5rem;
}

.page-input {
  box-sizing: border-box;
  min-width: 2ch;
  padding: .1rem .3rem;
  border: 1px solid var(--muted);
  border-radius: 4px;
  text-align: center;
}

.page-input::-webkit-outer-spin-button,
.page-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.page-input[type="number"] {
  appearance: textfield;
  -moz-appearance: textfield;
}

td {
  padding: clamp(.1rem, 0.45vw, .25rem) clamp(.35rem, 0.45vw, .5rem) !important;
  text-align: center;
  font-size: clamp(7px, 1.05vw, 10px) !important;

}

th {
  background: rgb(18, 19, 25);
  padding: .25rem 0;
  font-size: clamp(7px, 1.05vw, 10px) !important;
  border-bottom: 1px solid var(--muted);
  color: white;
  text-align: center;
}

.sell-color {
  color: #ffb300;
  font-weight: bold;
}

.buy-color {
  color: #27c1e6;
  font-weight: bold;
}

.rejected {
  color: rgb(220, 60, 60);
  font-weight: bold;
}
</style>
